import { Box, Button, HStack, IconButton, Input, VStack } from '@chakra-ui/react';
import { Check, Plus, Xmark } from 'iconoir-react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { GUIDELINE_ITEM_MAX_LENGTH } from './guideline-items';
import { useGuidelineRows } from './use-guideline-rows';

interface GuidelineListEditorProps {
  kind: 'dos' | 'donts';
  /** Card heading, repeated in each row's accessible name. */
  title: string;
  /** Example shown in the first row while the list is empty. */
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

/**
 * One outline input per guideline, in the order the creator reads them as
 * rows. Enter opens the next row, Backspace on an empty row removes it, a
 * multi-line or " · " paste is spread over rows. The remove button shows on
 * hover or focus, and always on touch screens where there is no hover.
 */
export const GuidelineListEditor = ({
  kind,
  title,
  placeholder,
  value,
  onChange,
}: GuidelineListEditorProps) => {
  const { t } = useTranslation();
  const {
    rows,
    registerInput,
    changeRow,
    insertAfter,
    removeRow,
    removeIfEmpty,
    pasteInto,
    addRow,
  } = useGuidelineRows(value, onChange);
  const isDos = kind === 'dos';
  const MarkerIcon = isDos ? Check : Xmark;
  const markerColor = isDos ? 'green.600' : 'fg.error';
  const listIsEmpty = rows.length === 1 && rows[0].text === '';

  const handleKeyDown = (id: string) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      insertAfter(id);
    } else if (event.key === 'Backspace' && removeIfEmpty(id)) {
      event.preventDefault();
    }
  };

  const handlePaste = (id: string) => (event: ClipboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const handled = pasteInto(
      id,
      event.clipboardData.getData('text'),
      input.selectionStart ?? input.value.length,
      input.selectionEnd ?? input.value.length
    );
    if (handled) event.preventDefault();
  };

  return (
    <VStack align="stretch" gap="1.5">
      {rows.map((row, index) => {
        const position = index + 1;
        return (
          <HStack key={row.id} className="group" gap="1.5">
            <Box as="span" display="inline-flex" flexShrink={0} color={markerColor} aria-hidden>
              <MarkerIcon width={13} height={13} />
            </Box>
            <Input
              ref={registerInput(row.id)}
              variant="outline"
              size="sm"
              fontSize="13px"
              color="primary.600"
              maxLength={GUIDELINE_ITEM_MAX_LENGTH}
              aria-label={t('collabs.briefTemplates.sections.guidelines.items.label', {
                title,
                position,
              })}
              placeholder={
                listIsEmpty
                  ? placeholder
                  : t('collabs.briefTemplates.sections.guidelines.items.placeholder')
              }
              value={row.text}
              onChange={(event) => changeRow(row.id, event.target.value)}
              onKeyDown={handleKeyDown(row.id)}
              onPaste={handlePaste(row.id)}
            />
            <IconButton
              aria-label={t('collabs.briefTemplates.sections.guidelines.items.remove', {
                title,
                position,
              })}
              variant="ghost"
              size="2xs"
              color="fg.muted"
              flexShrink={0}
              opacity="0"
              transition="opacity 0.12s"
              _groupHover={{ opacity: 1 }}
              _groupFocusWithin={{ opacity: 1 }}
              css={{ '@media (hover: none)': { opacity: 1 } }}
              onClick={() => removeRow(row.id)}
            >
              <Xmark width={13} height={13} />
            </IconButton>
          </HStack>
        );
      })}
      <Button
        variant="ghost"
        size="2xs"
        alignSelf="flex-start"
        color="fg.muted"
        fontWeight="medium"
        onClick={addRow}
      >
        <Plus width={13} height={13} />
        {t('collabs.briefTemplates.sections.guidelines.items.add')}
      </Button>
    </VStack>
  );
};
